<template>
  <loading-spinner v-if="isLoading" />
  <authenticated-layout v-if="userIsAuthenticated">
    <notification-component />
    <div class="min-h-full">
      <div>
        <main>
          <div>
            <span style="display:none"><ThemeToggler/></span>
            <router-view v-slot="{ Component }">
              <keep-alive :include="keptAliveDashboards">
                <component :is="Component" />
              </keep-alive>
            </router-view>
          </div>
        </main>
      </div>
    </div>
  </authenticated-layout>
</template>
<script>
import { Auth } from 'aws-amplify';
import { mapGetters } from 'vuex';
import httpClient from '@/httpClient';
import loadingSpinner from './components/loadingSpinner.vue';
import notificationComponent from './components/notificationComponent.vue';
import ThemeToggler from './components/themeToggle.vue';
import authenticatedLayout from './layouts/authenticatedLayout.vue';
import { clearStorage } from './storageSave';

/** List dashboards only — document/create views must not be cached. */
const KEPT_ALIVE_DASHBOARDS = [
  'IndexView',
  'ResourcingDashboard',
  'ViewAllDocs',
  'AllDocuments',
];

export default {
  name: 'App',
  components: {
    authenticatedLayout,
    ThemeToggler,
    notificationComponent,
    loadingSpinner,
  },
  provide() {
    return {
      signOut: this.signOut,
    };
  },
  data() {
    return {
      loaded: false,
      user: {},
      keptAliveDashboards: KEPT_ALIVE_DASHBOARDS,
    };
  },
  computed: {
    ...mapGetters({ theme: 'getTheme' }),
    userIsAuthenticated() {
      if (this.loaded) {
        return !!Auth?.user?.signInUserSession?.accessToken?.jwtToken || false;
      }
      return false;
    },
    isLoading() {
      return this.$store.getters.isLoading;
    },
  },
  watch: {
    // eslint-disable-next-line
    theme(newTheme, _oldTheme) {
      if (newTheme === 'light') {
        document.querySelector('html').classList.remove('dark');
      } else {
        document.querySelector('html').classList.add('dark');
      }
    },
  },
  async beforeMount() {
    this.$store.dispatch('setIsLoading', true);
    this.$store.dispatch('initTheme');
    try {
      await Auth.currentAuthenticatedUser();
      await Auth.currentSession();
      this.$store.dispatch('setIsLoading', false);
      this.loaded = true;
      this.user = await this.fetchUserProfile();
      if (!this.user?.userID) {
        this.$store.dispatch('setNotificationAll', {
          visible: true,
          type: 'error',
          title: 'Error!',
          message:
            'Could not find your user profile, please log a ticket in My Employee Services - https://equans.service-now.com/uk',
        });
        setTimeout(async () => await Auth.federatedSignIn(), 5000);
      } else {
        const userProfile = {
          ...Auth?.user.signInUserSession.idToken.payload,
          ...this.user,
        };
        this.$store.dispatch('setProfile', userProfile);
        this.$store.dispatch('setIsLoading', false);
        setInterval(async () => {
          await this.refreshUser();
        }, 1800000);
      }
    } catch {
      await Auth.federatedSignIn();
      this.$store.dispatch('setIsLoading', false);
    }
  },
  methods: {
    signOut: async () => {
      try {
        await Auth.signOut();
        clearStorage('auth');
      } catch {
        this.$store.dispatch('setNotificationAll', {
          visible: true,
          type: 'error',
          title: 'ERROR!',
          message: 'There was an error signing you out.',
        });
      }
    },
    fetchUserProfile: async () => {
      try {
        const userID =
          Auth.user.signInUserSession.idToken.payload.identities[0].userId.split(
            '@',
          )[0];
        const response = await httpClient.post('/users/user', {
          userID: userID.toUpperCase(),
        });
        return response.data.body?.Item ?? {};
      } catch {
        this.$store.dispatch('setNotificationAll', {
          visible: true,
          type: 'error',
          title: 'ERROR!',
          message: 'Could not get user profile data.',
        });
      }
    },
    refreshUser: async () => {
      try {
        const currentUser = await Auth.currentAuthenticatedUser();
        const currentSession = await Auth.currentSession();
        await currentUser.refreshSession(
          currentSession.refreshToken,
          (err, session) => {
            if (err) {
              Auth.federatedSignIn();
              return;
            }
            return session;
          },
        );
      } catch (err) {
        console.log('refresh error', err);
        Auth.federatedSignIn();
      }
    },
  },
};
</script>
<style>
  @import './assets/styles/print.css';
</style>
